import {
  ArrowLeft16,
  Download16,
  RotateClockwise16,
  RotateCounterclockwise16,
  ZoomIn16,
  ZoomOut16,
} from '@carbon/icons-react';
import React, { useCallback, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import styled from 'styled-components';
import {
  CpBody01Primary,
  // CpBody01Secondary,
} from 'components/atoms/customer-portal/typography';
import { Loading } from 'components/atoms/loading';
import { useAttachmentById } from 'queries/attachments';
import { handleDownload } from 'utils/attachment';
import { isImageUrlFromExtension } from 'utils/files';
// import { fullName } from 'utils/full-name';

const Page = styled.div`
  height: 100%;
  width: 100%;
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
`;

const TitleBar = styled.div`
  width: 100%;
  /* background: black; */
  background: var(--cp-black-opacity-80-hover);
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--cp-spacing-05);
  position: fixed;
  top: 0;
  left: 0;
  padding-left: var(--cp-spacing-06);
  padding-right: var(--cp-spacing-06);
  padding-top: var(--cp-spacing-04);
  padding-bottom: var(--cp-spacing-04);
  --cp-text-primary: var(--cp-white-100);
  --cp-text-secondary: var(--cp-white-100);
  p {
    margin-bottom: 0;
  }
  svg {
    fill: var(--cp-white-100);
  }
  z-index: 100;
`;

const BackWrapper = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  svg {
    margin-right: var(--cp-spacing-03);
  }
`;

const BackIcon = styled.div`
  padding: var(--cp-spacing-03);
  display: flex;
  align-items: center;
  justify-content: center;
`;

// const EmbeddedBy = styled(CpBody01Secondary)`
//   @media (max-width: 768px) {
//     display: none;
//   }
// `;

// const AnnotateWrapper = styled.div`
//   padding-top: var(--cp-spacing-03);
//   padding-bottom: var(--cp-spacing-03);
//   padding-left: var(--cp-spacing-05);
//   padding-right: var(--cp-spacing-05);
//   display: flex;
//   align-items: center;
//   justify-content: center;
//   svg {
//     margin-right: var(--cp-spacing-03);
//   }
//   border-radius: 2rem;
//   /* cursor: pointer; */
//   border: 1px solid var(--cp-white-100);
//   visibility: hidden;
// `;

const ActionsWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: end;
`;

const ActionIcon = styled.div`
  cursor: pointer;
  padding: var(--cp-spacing-03);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: var(--cp-spacing-03);

  @media (max-width: 480px) {
    display: none;
  }

  &:hover {
    background: var(--cp-white-opacity-50-hover);
    border-radius: var(--border-radius-default);
  }

  &:active {
    background: var(--cp-white-opacity-60-hover);
  }
`;

const Content = styled.div`
  height: 100vh;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #003147;
`;

const ImageWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #003147;
  max-width: 90%;
  max-height: 90%;
  overflow: auto;
`;

const Image = styled.img<{
  rotate: number;
  zoom: number;
}>`
  width: 100vw;
  height: 80vh;
  object-fit: contain;
  transform: rotate(${(props) => props.rotate}deg)
    scale(${(props) => props.zoom});
  pointer-events: none;
  user-select: none;
  transition: transform 0.3s;
`;

enum FileTypes {
  IMAGE,
  PDF,
  OTHER,
}

export const CpFilesPreview: React.FC<{
  projectId: number;
  attachmentId: number;
}> = ({ attachmentId }) => {
  const router = useHistory();
  const { data: attachment } = useAttachmentById(attachmentId);
  // const user = useGetUserByUserId(attachment?.createdBy);
  // const translate = useTranslate();
  const [rotate, setRotate] = useState(0);
  const [zoom, setZoom] = useState(1);

  const fileType = useMemo(() => {
    if (!attachment?.name) {
      return FileTypes.OTHER;
    }

    const isImage = isImageUrlFromExtension(attachment?.name);

    if (isImage) {
      return FileTypes.IMAGE;
    }

    const isPDf = attachment?.name.endsWith('.pdf');

    if (isPDf) {
      return FileTypes.PDF;
    }

    return FileTypes.OTHER;
  }, [attachment?.name]);

  // const formattedCreatedOn = useMemo(() => {
  //   return moment(attachment?.createdAt).format('DD MMM YYYY');
  // }, [attachment?.createdAt]);

  const onBack = useCallback(() => {
    // go back if history is available
    if (router.length > 1) {
      router.goBack();

      return;
    }
    // else go to root
    router.push('/');
  }, [router]);

  const onDownload = useCallback(() => {
    if (attachmentId) {
      handleDownload(attachmentId);
    }
  }, [attachmentId]);

  const onRotateClockwise = useCallback(() => {
    setRotate((prev) => prev + 90);
  }, []);

  const onRotateCounterClockwise = useCallback(() => {
    setRotate((prev) => prev - 90);
  }, []);

  const onZoomIn = useCallback(() => {
    setZoom((prev) => prev + 0.1);
  }, []);

  const onZoomOut = useCallback(() => {
    setZoom((prev) => prev - 0.1);
  }, []);

  if (!attachment) {
    return (
      <LoadingWrapper>
        <Loading size="large" />
      </LoadingWrapper>
    );
  }

  return (
    <Page>
      <TitleBar>
        <BackWrapper>
          <BackIcon onClick={onBack}>
            <ArrowLeft16 />
          </BackIcon>
          <CpBody01Primary>{attachment?.name}</CpBody01Primary>
        </BackWrapper>
        <ActionsWrapper>
          <ActionIcon onClick={onDownload} data-cy="cp.files.preview.download">
            <Download16 />
          </ActionIcon>
          <ActionIcon
            onClick={onRotateClockwise}
            data-cy="cp.files.preview.rotate-clockwise"
          >
            <RotateClockwise16 />
          </ActionIcon>
          <ActionIcon
            onClick={onRotateCounterClockwise}
            data-cy="cp.files.preview.rotate-counter-clockwise"
          >
            <RotateCounterclockwise16 />
          </ActionIcon>
          <ActionIcon onClick={onZoomIn} data-cy="cp.files.preview.zoom-in">
            <ZoomIn16 />
          </ActionIcon>
          <ActionIcon onClick={onZoomOut} data-cy="cp.files.preview.zoom-out">
            <ZoomOut16 />
          </ActionIcon>
        </ActionsWrapper>
        {/* <AnnotateWrapper>
          <Edit16 />
          <CpBody01Primary>
            <FormattedMessage id="customer_portal.file_preview.annotate" />
          </CpBody01Primary>
        </AnnotateWrapper> */}
      </TitleBar>
      <Content>
        {fileType === FileTypes.IMAGE && (
          <ImageWrapper>
            <Image
              src={attachment?.location}
              alt={attachment?.name}
              rotate={rotate}
              zoom={zoom}
            />
          </ImageWrapper>
        )}
      </Content>
    </Page>
  );
};
